<div class="mb-4 rounded-xl bg-white shadow-sm md:mb-8">
  @if (isLoading) {
    <div class="flex h-40 items-center justify-center">
      <mat-progress-spinner
        mode="indeterminate"
        color="primary"
        diameter="40"
      ></mat-progress-spinner>
    </div>
  } @else {
    <div
      class="flex flex-col gap-4 bg-slate-50 px-4 py-2 md:flex-row md:justify-between"
    >
      <div>
        <mat-form-field appearance="outline" class="min-w-80">
          <mat-icon matPrefix>search</mat-icon>
          <input
            matInput
            (input)="searchTerm$.set($event.target['value']?.toLowerCase())"
            placeholder="Search device"
            autocomplete="off"
            type="search"
          />
        </mat-form-field>
      </div>
      <div class="flex items-center justify-center gap-4 md:justify-end">
        <div class="flex">
          @for (level of severityCheckboxes | keyvalue; track level.key) {
            <div
              class="inline-block border-b-4 pr-2"
              [ngClass]="{
                'border-orange-200 hover:border-orange-400':
                  level.key === SeverityLevel.Investigate,
                'border-yellow-200 hover:border-yellow-400':
                  level.key === SeverityLevel.Monitor,
                'border-green-200 hover:border-green-400':
                  level.key === SeverityLevel.Observe,
              }"
            >
              <mat-checkbox
                (change)="level.value.set($event.checked)"
                [checked]="level.value()"
                class="rounded-xl"
              >
                {{ level.key }}
              </mat-checkbox>
            </div>
          }
        </div>
      </div>
    </div>

    @if (filteredDevices$().length === 0) {
      <div class="p-4 text-center italic text-gray-600">
        No devices found matching your criteria.
      </div>
    } @else {
      <div class="flex pb-6">
        <!-- Left: Device List -->
        <div class="w-2/5 p-4 md:w-1/3">
          <ul>
            @for (device of filteredDevices$(); track device.deviceName) {
              <li
                class="flex cursor-pointer items-center justify-between rounded-xl border border-gray-300 p-2 shadow-sm transition hover:bg-blue-50 md:px-4 md:py-3"
                (click)="selectedDevice$.set(device)"
                [ngClass]="[
                  selectedDevice$()?.deviceName === device.deviceName
                    ? 'bg-blue-100'
                    : 'bg-gray-50',
                ]"
              >
                <div>
                  <div
                    class="text-sm font-semibold text-gray-900 md:text-base md:font-medium"
                  >
                    {{ device.deviceName }}
                  </div>
                  <div class="text-xs text-gray-600 md:text-sm">
                    {{ device.events.length }}
                    {{ device.events.length || 0 | pluralize: 'Event' }}
                    <span class="hidden text-gray-600 md:inline"
                      >• Latest event:
                      {{ device.events[0].date | date: 'MMM d, y' }}</span
                    >
                  </div>
                </div>
                <mat-icon class="text-gray-500">chevron_right</mat-icon>
              </li>
            }
          </ul>
        </div>

        <!-- Right: Device Details -->
        <div class="flex-1 py-4 pl-0 pr-4 md:pl-4">
          <div class="mb-2 pb-4">
            <h3
              class="flex flex-col justify-between text-xl font-normal text-gray-800 md:flex-row md:text-2xl"
            >
              <div>{{ selectedDevice$()?.deviceName || 'None selected' }}</div>
              @if (selectedDevice$()?.deviceName) {
                <div class="text-base font-light text-gray-600 md:text-lg">
                  {{ selectedDevice$()?.events.length || 0 }} Observation
                  {{
                    selectedDevice$()?.events.length || 0
                      | pluralize: 'Summary' : 'Summaries'
                  }}
                </div>
              }
            </h3>

            @if (selectedDevice$()?.deviceName) {
              <p class="text-xs italic text-gray-600 md:text-sm">
                (This output is generated using AI and may contain inaccuracies;
                please verify with source data before taking actions)
              </p>
            }
          </div>

          @if (selectedDevice$()?.deviceName) {
            <div>
              <table
                class="mb-4 min-w-full table-auto rounded-xl border border-gray-200 font-normal"
              >
                <thead class="bg-gray-50">
                  <tr
                    class="text-left text-xs uppercase tracking-wider text-gray-600"
                  >
                    <th class="px-4 py-2">Date</th>
                    <th class="px-4 py-2">Time</th>
                    <th class="hidden px-4 py-2 md:table-cell">Severity</th>
                    <th class="hidden px-4 py-2 md:table-cell">Lead Count</th>
                  </tr>
                </thead>
                <tbody class="text-sm text-gray-700">
                  @for (
                    event of filteredSelectedDeviceEvents$().slice(
                      pageNumber * 8,
                      (pageNumber + 1) * 8
                    );
                    track $index
                  ) {
                    <tr class="border-t border-gray-300">
                      <td class="px-4 py-2 text-sm">
                        {{ event.date | date: 'MMM d, y' }}
                      </td>
                      <td class="px-4 py-2 text-sm">
                        {{ event.begin_time }} – {{ event.end_time }}
                      </td>
                      <td class="hidden px-4 py-2 md:table-cell">
                        <span
                          class="inline-block w-auto rounded-full px-2 py-1 text-center text-xs font-semibold text-white"
                          [ngClass]="{
                            'bg-orange-600':
                              event.action_label === SeverityLevel.Investigate,
                            'bg-orange-500':
                              event.action_label === SeverityLevel.Monitor,
                            'bg-green-500':
                              event.action_label === SeverityLevel.Observe,
                          }"
                        >
                          {{ event.action_label }}
                        </span>
                      </td>
                      <td class="hidden px-4 py-2 md:table-cell">
                        {{ event.lead_count }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td colspan="4" class="px-4 py-2">
                        <div class="flex items-center gap-2">
                          <div
                            class="hidden aspect-square max-h-min rounded-full border-2 border-blue-950 md:flex"
                          >
                            <mat-icon class="text-blue-950">
                              smart_toy
                            </mat-icon>
                          </div>
                          <div>
                            <div
                              class="flex items-center gap-2 font-semibold md:hidden"
                            >
                              <span class="text-black">Severity:</span>
                              <div
                                class="inline-block w-auto rounded-full px-2 py-1 text-center text-sm font-semibold text-white"
                                [ngClass]="{
                                  'bg-orange-600':
                                    event.action_label ===
                                    SeverityLevel.Investigate,
                                  'bg-orange-500':
                                    event.action_label ===
                                    SeverityLevel.Monitor,
                                  'bg-green-500':
                                    event.action_label ===
                                    SeverityLevel.Observe,
                                }"
                              >
                                {{ event.action_label }}
                              </div>
                            </div>
                            <div
                              class="flex py-1 text-sm font-semibold text-black md:hidden"
                            >
                              Lead Count: {{ event.lead_count }}
                            </div>
                            <div class="text-sm">
                              {{ event.summary }}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>

              <mat-paginator
                (page)="pageNumber = $event.pageIndex"
                [length]="filteredSelectedDeviceEvents$().length"
                [pageIndex]="pageNumber"
                [pageSize]="8"
              ></mat-paginator>
            </div>
          } @else {
            <p class="text-sm italic text-gray-600">
              Select a device to see details.
            </p>
          }
        </div>
      </div>
    }
  }
</div>
